import { useApi } from '@/src/store/api.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamChallengeDetails } from '@/src/store/jam-challenge-details.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { JamEventSupportDetails } from '@/src/types/JamChallengeSupportDetails';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { safeString } from '@/src/utils/string.utils';
import {
  Box,
  Button,
  Container,
  ContentLayout,
  Flashbar,
  Grid,
  Header,
  SpaceBetween,
  TextContent,
  Textarea,
} from '@amzn/awsui-components-react';
import { unescape } from 'lodash';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import JamChallengeHeader from '../JamChallengeHeader/JamChallengeHeader';

const JamFacilitatorNotes = () => {
  const { t } = useTranslation();
  const { jamFacilitatorApi } = useApi();
  const { selectedJamChallenge } = useJamChallengeDetails();
  const { eventName } = useJamEventDetails();
  const { clearFlashbars } = useFlashbars();
  const [supportDetails, setSupportDetails] = useState<JamEventSupportDetails>();
  const [notes, setNotes] = useState('');
  const [editedNotes, setEditedNotes] = useState('');
  const [editMode, setEditMode] = useState(false);

  const getSupportDetails = async () => {
    const eventSupportDetails: JamEventSupportDetails = await jamFacilitatorApi.getEventSupportDetails(eventName);

    setSupportDetails(eventSupportDetails);
  };

  useEffect(() => {
    if (!supportDetails || !selectedJamChallenge?.id) {
      return;
    }

    const challengeSupportDetails = supportDetails.challenges.find(
      (details) => details.id === selectedJamChallenge?.id
    );

    if (!challengeSupportDetails) {
      return;
    }

    setNotes(challengeSupportDetails.facilitatorNotes || '');
  }, [supportDetails, selectedJamChallenge]);

  const handleToggleEditMode = (newEditMode: boolean) => {
    setEditMode(newEditMode);

    if (newEditMode) {
      setEditedNotes(notes);
    }
  };

  useLayoutEffect(() => {
    clearFlashbars();
    void getSupportDetails();
  }, []);

  const onSaveNotes = () => {
    if (!eventName || !selectedJamChallenge?.id || !editedNotes) {
      return;
    }

    void jamFacilitatorApi
      .addFacilitatorNotes(safeString(eventName), safeString(selectedJamChallenge?.id), safeString(editedNotes))
      .then(() => {
        setEditMode(false);
        void getSupportDetails();
      });
  };

  return (
    <ContentLayout header={<Header variant="h1">{selectedJamChallenge?.title ?? 'Untitled Jam Challenge'}</Header>}>
      <SpaceBetween direction="vertical" size="xxl">
        <JamChallengeHeader hideProperties />
        <SpaceBetween size="m">
          <SpaceBetween direction="vertical" size="xs">
            <Flashbar
              items={[
                {
                  type: 'info',
                  content: t(i18nKeys.myJams.challenges.details.facilitatorNotes.alert),
                  id: 'facilitator-notes-alert',
                },
              ]}
            />
            <Flashbar
              items={[
                {
                  type: 'info',
                  content: t(i18nKeys.myJams.challenges.details.facilitatorNotes.notesInstructions),
                  id: 'facilitator-notes-instructions',
                },
              ]}
            />
          </SpaceBetween>
          <Container
            className="comments-wrapper"
            header={<Header>{t(i18nKeys.myJams.challenges.details.facilitatorNotes.title)}</Header>}
          >
            {!editMode && (
              <Box margin={{ top: 'l' }}>
                <Grid gridDefinition={[{ colspan: 11 }, { colspan: 1 }]}>
                  <TextContent>
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {notes ? unescape(notes) : t(i18nKeys.myJams.challenges.details.facilitatorNotes.emptyNote)}
                    </div>
                  </TextContent>
                  <Button variant="link" onClick={() => handleToggleEditMode(true)} data-testid="add-note-button">
                    {t(i18nKeys.myJams.challenges.details.facilitatorNotes.add)}
                  </Button>
                </Grid>
              </Box>
            )}
            {editMode && (
              <Box margin={{ top: 'l' }}>
                <SpaceBetween direction="vertical" alignItems="end" size="s">
                  <SpaceBetween size="l" direction="horizontal">
                    <Button variant="link" onClick={() => handleToggleEditMode(false)} data-testid="cancel-note-button">
                      {t(i18nKeys.myJams.challenges.details.facilitatorNotes.cancel)}
                    </Button>
                    <Button variant="primary" onClick={() => onSaveNotes()}>
                      {t(i18nKeys.myJams.challenges.details.facilitatorNotes.save)}
                    </Button>
                  </SpaceBetween>
                </SpaceBetween>
                <Box margin={{ top: 's' }}>
                  <Textarea
                    value={editedNotes}
                    onChange={({ detail }) => setEditedNotes(detail.value)}
                    data-testid="notes-input"
                  />
                  <small>{t(i18nKeys.general.avoidPIIWarning)}</small>
                </Box>
              </Box>
            )}
          </Container>
        </SpaceBetween>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default JamFacilitatorNotes;
